import {
  type JSX,
  type SVGProps,
  type ReactNode,
  type ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { assetConstants } from '@chainflip/utils/chainflip';
import { brokerAliasMap } from '@chainflip/utils/consts';
import { abbreviate } from '@chainflip/utils/string';
import classNames from 'classnames';
import Checkbox from '@/shared/components/flip-ui-kit/Checkbox';
import { DualRangeSlider } from '@/shared/components/flip-ui-kit/DualRangeSlider';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import { type SwapRequestOverviewFragment } from '@/shared/graphql/generated/reporting/graphql';
import useDebouncedEffect from '@/shared/hooks/useDebouncedEffect';
import useSupportedAssets from '@/shared/hooks/useSupportedAssets';
import BoltIcon from '@/shared/icons/flip-ui-kit/large/Bolt';
import CheckCircleIcon from '@/shared/icons/flip-ui-kit/large/CheckCircle';
import ChunksIcon from '@/shared/icons/flip-ui-kit/large/Chunks';
import CloseCircleIcon from '@/shared/icons/flip-ui-kit/large/CloseCircle';
import FileTextIcon from '@/shared/icons/flip-ui-kit/large/FileText';
import HourglassIcon from '@/shared/icons/flip-ui-kit/large/Hourglass';
import PulseIcon from '@/shared/icons/flip-ui-kit/large/Pulse';
import RefundIcon from '@/shared/icons/flip-ui-kit/large/Refund';
import StopIcon from '@/shared/icons/flip-ui-kit/large/Stop';
import TransferIcon from '@/shared/icons/flip-ui-kit/large/Transfer';
import VaultIcon from '@/shared/icons/flip-ui-kit/large/Vault';
import WarningTriangleIcon from '@/shared/icons/flip-ui-kit/small/WarningTriangle';
import { chainLabel, compareAssets } from '@/shared/utils/chainflip';
import { formatWithCommas, formatWithNumeral } from '@/shared/utils/numbers';
import { removeCommas } from '@/shared/utils/strings';
import { useAllSwapsFilterStore, type SwapType } from '../../../hooks/useFilterStores';

export const MAX_SWAP_SIZE = 5_000_000 as const;

const inputClassName =
  'rounded-md border border-cf-gray-5 bg-cf-gray-3-5 p-2 text-cf-light-2 transition ease-in hover:bg-cf-gray-5 hover:text-white focus:border-cf-light-2 outline-none focus:border-transparent';

export const swapStateOptions: {
  label: string;
  icon: (props?: SVGProps<SVGSVGElement>) => JSX.Element;
  value: NonNullable<SwapRequestOverviewFragment['status']>;
}[] = [
  {
    label: 'Completed',
    icon: CheckCircleIcon,
    value: 'SUCCESS',
  },
  {
    label: 'In progress',
    icon: HourglassIcon,
    value: 'IN_PROGRESS',
  },
  {
    label: 'Refunded',
    icon: RefundIcon,
    value: 'REFUND_SUCCESS',
  },
  {
    label: 'Partially refunded',
    icon: RefundIcon,
    value: 'PARTIAL_REFUND',
  },
  {
    label: 'Failed',
    icon: CloseCircleIcon,
    value: 'SWAP_FAILED',
  },
  {
    label: 'Stopped',
    icon: StopIcon,
    value: 'STOPPED',
  },
] as const;

export const SwapStateSelection = () => {
  const { swapStates, setFilter } = useAllSwapsFilterStore();

  return swapStateOptions.map(({ value, icon, label }) => (
    <div key={value} className="flex items-center gap-x-2 p-3">
      <Checkbox
        id={value}
        checked={swapStates.includes(value)}
        setChecked={(checked) =>
          setFilter(
            'swapStates',
            checked ? [...swapStates, value] : swapStates.filter((s) => s !== value),
          )
        }
      />
      <span className="text-cf-light-2">{icon()}</span>
      <label htmlFor={value} className="font-aeonikMedium text-cf-light-4">
        {label}
      </label>
    </div>
  ));
};

export const AssetSelection = ({ direction }: { direction: 'from' | 'to' }) => {
  const { assetsFrom, assetsTo, setFilter } = useAllSwapsFilterStore();
  const chainflipAssets = useSupportedAssets();

  const assetState = direction === 'from' ? assetsFrom : assetsTo;

  const assets = useMemo(() => [...chainflipAssets].sort(compareAssets), []);

  return assets.map((asset) => (
    <div key={asset} className="flex items-center gap-x-2 p-3">
      <Checkbox
        id={asset}
        checked={assetState.includes(asset)}
        setChecked={(value) =>
          setFilter(
            direction === 'from' ? 'assetsFrom' : 'assetsTo',
            value ? [...assetState, asset] : assetState.filter((a) => a !== asset),
          )
        }
      />
      <TokenWithChainByAsset asset={asset} size="small" displayChainLogo />
      <label
        htmlFor={asset}
        className="flex items-center gap-x-2 font-aeonikMedium text-cf-light-4"
      >
        <span className="text-cf-light-4">{assetConstants[asset].symbol}</span>
        <span className="text-cf-light-2">on {chainLabel(assetConstants[asset].chain)}</span>
      </label>
    </div>
  ));
};

export const swapTypeOptions: {
  label: string;
  icon: ReactNode;
  value: SwapType;
}[] = [
  {
    label: 'Boost',
    icon: <BoltIcon />,
    value: 'isBoosted',
  },
  {
    label: 'DCA',
    icon: <ChunksIcon />,
    value: 'isDca',
  },
  {
    label: 'CCM',
    icon: <FileTextIcon />,
    value: 'isCcm',
  },
  {
    label: 'Vault',
    icon: <VaultIcon />,
    value: 'isVaultSwap',
  },
  {
    label: 'Internal',
    icon: <TransferIcon />,
    value: 'isOnChain',
  },
  {
    label: 'Liquidation',
    icon: <PulseIcon />,
    value: 'isLiquidation',
  },
] as const;

export const SwapTypeSelection = () => {
  const { swapTypes, setFilter } = useAllSwapsFilterStore();

  return swapTypeOptions.map(({ value, icon, label }) => (
    <div key={value} className="flex items-center gap-x-2 p-3">
      <Checkbox
        id={value}
        checked={swapTypes.includes(value)}
        setChecked={(checked) =>
          setFilter(
            'swapTypes',
            checked ? [...swapTypes, value] : swapTypes.filter((s) => s !== value),
          )
        }
      />
      <span className="text-cf-light-2">{icon}</span>
      <label htmlFor={value} className="font-aeonikMedium text-cf-light-4">
        {label}
      </label>
    </div>
  ));
};

export const BrokerSelection = () => {
  const { brokers, setFilter } = useAllSwapsFilterStore();
  const [search, setSearch] = useState('');

  const sortedBrokers = useMemo(
    () =>
      Object.entries(brokerAliasMap)
        .sort(([, a], [, b]) => a.name.localeCompare(b.name))
        .map(([id, broker]) => ({ id, ...broker })),
    [brokerAliasMap],
  );

  const filteredBrokers = sortedBrokers.filter(
    (broker) =>
      broker.name.toLowerCase().includes(search.toLowerCase()) ||
      broker.id.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="flex flex-col">
      <div className="w-[220px] px-2 pt-2">
        <input
          maxLength={100}
          placeholder="Search for broker..."
          className={classNames(inputClassName, 'w-full')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {filteredBrokers.map(({ id: idSs58, name }) => (
        <div key={idSs58} className="flex items-center gap-x-2 p-3">
          <Checkbox
            id={idSs58}
            checked={brokers.includes(idSs58)}
            setChecked={(checked) =>
              setFilter(
                'brokers',
                checked ? [...brokers, idSs58] : brokers.filter((s) => s !== idSs58),
              )
            }
          />
          <label
            htmlFor={idSs58}
            className="flex items-center gap-x-1 font-aeonikMedium text-cf-light-4"
          >
            <span>{name}</span>
            <span className="font-aeonikRegular text-cf-light-2">{abbreviate(idSs58)}</span>
          </label>
        </div>
      ))}
    </div>
  );
};

export const AddressSelection = ({ type }: { type: 'refund' | 'destination' }) => {
  const { refundAddress, destinationAddress, setFilter } = useAllSwapsFilterStore();

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    const newValue = e.target.value;
    if (/^[0-9a-z]*$/i.test(newValue)) {
      setFilter(type === 'refund' ? 'refundAddress' : 'destinationAddress', newValue);
    }
  }, []);

  return (
    <div className="w-[262px] p-2">
      <input
        maxLength={100}
        placeholder="Enter or paste address..."
        className={classNames(inputClassName, 'w-full')}
        value={type === 'refund' ? (refundAddress ?? '') : (destinationAddress ?? '')}
        onChange={onChange}
      />
    </div>
  );
};

export const SwapSizeSelection = () => {
  const { swapSizeRange: storeRange, setFilter } = useAllSwapsFilterStore();
  const [range, setRange] = useState(storeRange);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (type: 'min' | 'max', value: string) => {
    const stripped = removeCommas(value);
    if (!/^\d*$/.test(stripped)) return;

    const number = Number(stripped);
    if (number < 0 || number > MAX_SWAP_SIZE) {
      setError('amount is out of range');
      return;
    }

    const isMin = type === 'min';
    const otherValue = isMin ? range[1] : range[0];
    const isInvalid = isMin ? number > otherValue : number < otherValue;

    if (isInvalid) {
      setError(
        `${isMin ? 'Min' : 'Max'}. value must be ${isMin ? 'less' : 'greater'} than ${isMin ? 'max' : 'min'}. value`,
      );
      return;
    }

    setError(null);
    setRange(isMin ? [number, otherValue] : [otherValue, number]);
  };

  useDebouncedEffect(
    () => {
      setFilter('swapSizeRange', range);
    },
    [range],
    500,
  );

  // needed because reset is called from outside component
  useEffect(() => {
    if (storeRange[0] !== range[0] || storeRange[1] !== range[1]) {
      setRange(storeRange);
    }
  }, [storeRange]); // do not include range

  return (
    <div className="flex flex-col gap-y-3 p-3">
      <div className="text-12">Value range</div>
      <div className="w-[262px]">
        <DualRangeSlider
          max={5_000_000}
          sliderValues={range}
          setSliderValues={setRange}
          format={(v) => `$${formatWithNumeral(v)}`}
        />
      </div>
      <div className="flex items-center gap-x-2">
        <div className="flex flex-col gap-y-1">
          <div>Min. value</div>
          <input
            className={classNames(inputClassName, 'w-[122px]')}
            value={formatWithCommas(range[0])}
            onChange={(e) => handleChange('min', e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-y-1">
          <div>Max. value</div>
          <input
            className={classNames(inputClassName, 'w-[122px]')}
            value={formatWithCommas(range[1])}
            onChange={(e) => handleChange('max', e.target.value)}
          />
        </div>
      </div>
      {error && (
        <div className="flex items-center gap-x-1 text-cf-red-1">
          <WarningTriangleIcon />
          <div className="text-12">{error}</div>
        </div>
      )}
    </div>
  );
};
